import { useEffect, useState } from 'react';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { msalInstance } from './lib/msal';
import { useAuth } from './hooks/useAuth';
import { isLocalMode } from './lib/localMode';
import { canRecordAttempt, signinAlreadyAttempted, markSigninAttempted, clearSigninAttempt } from './lib/signinAttempt';
import { DeploymentEngineProvider } from './services/deployment';
import { EngineProvider } from './services/engine';
import { GitServiceProvider } from './services/git';
import { PipelineServiceProvider } from './services/pipeline';
import { ConnectionServiceProvider } from './services/connections';
import { MetadataServiceProvider } from './services/metadata';
import { SelectionSetServiceProvider } from './services/selectionSets';
import { PromotionServiceProvider } from './services/promotion';
import { CmlServiceProvider } from './services/cml';
import { DeploymentErrorsServiceProvider } from './services/deploymentErrors/DeploymentErrorsServiceContext';
import { AppProvider } from './context/AppContext';
import { SessionProvider } from './context/SessionContext';
import { ProjectProvider } from './context/ProjectContext';
import { SessionGate } from './components/layout/SessionGate';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './components/ui/Toast';
import { router } from './router';

/**
 * Read at CALL time, not captured at import. A module-scope constant fixes the answer when the
 * module first loads, so no test can exercise both modes in one run — and an untestable config
 * decision is how two mock services reached production. Matches useAuth and SessionContext, which
 * read it the same way.
 */
function mockAuth(): boolean {
  return import.meta.env.VITE_MOCK_AUTH === 'true';
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,        // 30 s — avoid redundant fetches on nav
      retry: 1,
    },
  },
});

// ── AuthGate ──────────────────────────────────────────────────────────────────
//
// Wraps authenticated content and redirects unauthenticated users to AAD login.
// Rendered inside all service providers so login() / logout() can access MSAL.

function AuthGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, settled, login, loginError } = useAuth();
  const [loopBlocked, setLoopBlocked] = useState(false);

  // Start sign-in only once MSAL has SETTLED. Before that, "no account" means "not known yet" —
  // during startup, or while a redirect return is still being processed — and calling login() then
  // is an interaction MSAL rejects, leaving the user on "Redirecting to login…" indefinitely.
  //
  // And start it AT MOST ONCE per tab. This redirect used to be unbounded: when msal-react@5 was
  // installed against msal-browser@3 the redirect coming back from Entra was never processed, so
  // `accounts` stayed empty, the gate signed in again, and the user was bounced five or six times
  // until Entra throttled them onto the sign-in screen with no explanation. The version pin fixes
  // that cause; the bound means the next cause surfaces as a message instead of a loop.
  useEffect(() => {
    if (mockAuth()) return;
    // The desktop app authenticates against its own loopback token, never Entra — so it takes none of
    // the sign-in path below, including the loop guard, which has nothing to guard against here.
    if (isLocalMode()) return;
    if (isAuthenticated) { clearSigninAttempt(); setLoopBlocked(false); return; }
    if (!settled) return;
    if (!canRecordAttempt() || signinAlreadyAttempted()) { setLoopBlocked(true); return; }
    markSigninAttempted();
    login();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, settled]);

  // Desktop app, or mock-auth dev mode: render children directly — no MSAL templates needed.
  //
  // The MSAL templates would render nothing at all in the desktop app: there is no account, so
  // `UnauthenticatedTemplate` would win and the window would sit on “Redirecting to login…” forever,
  // waiting for a tenant that does not exist.
  if (mockAuth() || isLocalMode()) {
    return <>{children}</>;
  }

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <div className="min-h-screen bg-canvas text-fg-muted flex items-center justify-center px-6 text-sm">
          {loopBlocked && loginError === null ? (
            /* The bound tripped: sign-in was attempted and we came back still signed out. Saying so
               beats a second redirect — the user can act on this, and cannot act on being bounced. */
            <div data-testid="auth-loop-blocked" className="w-full max-w-md rounded-card border border-del-line bg-surface p-6 text-left">
              <h1 className="text-base font-semibold text-fg">Stopped a sign-in loop</h1>
              <p className="mt-1.5 text-[13px] leading-relaxed text-fg-muted">
                Stride sent you to sign in and you came back still signed out, so it stopped rather than
                sending you again. Reload to try once more. If it keeps happening, the app registration’s
                redirect URI or the sign-in libraries are misconfigured — the browser console will name it.
              </p>
              <button
                type="button"
                data-testid="auth-loop-retry"
                onClick={() => { clearSigninAttempt(); window.location.reload(); }}
                className="mt-4 rounded-control border border-line px-3 py-1.5 text-[13px] font-medium text-fg hover:bg-surface-hover"
              >Try again</button>
            </div>
          ) : loginError !== null ? (
            /* A failed sign-in must SAY so. "Redirecting to login…" forever is the same screen as a
               redirect that is simply slow, and the user can't tell which they are looking at. */
            <div data-testid="auth-login-error" className="w-full max-w-md rounded-card border border-del-line bg-surface p-6 text-left">
              <h1 className="text-base font-semibold text-fg">Sign-in failed</h1>
              <p className="mt-1.5 text-[13px] leading-relaxed text-fg-muted">
                Stride couldn’t complete sign-in. If this persists, the app registration or its redirect
                URI is likely misconfigured.
              </p>
              <p className="mt-2 rounded-md bg-surface-2 px-3 py-2 font-mono text-[11px] leading-snug text-fg-subtle">{loginError}</p>
              <button
                type="button" data-testid="auth-login-retry" onClick={() => window.location.reload()}
                className="mt-4 rounded-control border border-accent bg-accent px-3 py-1.5 text-xs font-semibold text-accent-fg hover:bg-accent-600"
              >Try again</button>
            </div>
          ) : (
            <span data-testid="auth-redirecting">Redirecting to login…</span>
          )}
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <MsalProvider instance={msalInstance}>
        <DeploymentEngineProvider>
          <EngineProvider>
          <PipelineServiceProvider>
          <ConnectionServiceProvider>
          <MetadataServiceProvider>
          <SelectionSetServiceProvider>
          <PromotionServiceProvider>
          <CmlServiceProvider>
          <DeploymentErrorsServiceProvider>
          <GitServiceProvider>
            <AppProvider>
              <ToastProvider>
                <AuthGate>
                  {/* MSAL proves a tenant sign-in; SessionProvider asks the SERVER what this person
                      may actually do, and SessionGate renders the app only once that is known. The
                      order matters: /api/me needs a token, so it must sit inside AuthGate. */}
                  <SessionProvider>
                    <SessionGate>
                      {/* Inside SessionGate: the project list comes from /api/me, so there is nothing
                          to provide until the session has resolved. */}
                      <ProjectProvider>
                        <RouterProvider router={router} />
                      </ProjectProvider>
                    </SessionGate>
                  </SessionProvider>
                </AuthGate>
              </ToastProvider>
            </AppProvider>
          </GitServiceProvider>
          </DeploymentErrorsServiceProvider>
          </CmlServiceProvider>
          </PromotionServiceProvider>
          </SelectionSetServiceProvider>
          </MetadataServiceProvider>
          </ConnectionServiceProvider>
          </PipelineServiceProvider>
          </EngineProvider>
        </DeploymentEngineProvider>
      </MsalProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
